import type { Collaborators, Reviewer, TeamReviewerSelection } from '@/types/github';

import { ReviewerState } from '@/types/github';

export type PendingSelection = {
  toAdd: Set<string>;
  toRemove: Set<string>;
};

export const EMPTY_COLLABORATORS: Collaborators = [];

/**
 * Filters out the PR author from the list of available reviewers
 */
export function filterAvailableReviewers(
  collaborators: Collaborators,
  authorLogin: string,
): Collaborators {
  return collaborators.filter((c) => c.login !== authorLogin);
}

/**
 * Determines if a reviewer can be removed from a PR
 * @returns True if the reviewer can be removed (only in REQUESTED_REVIEW state), false otherwise
 */
export function canRemoveReviewer(reviewer: Pick<Reviewer, 'state'>): boolean {
  // Only allow removal if the reviewer hasn't taken any action
  // (i.e., they are only in REQUESTED_REVIEW state)
  return reviewer.state === ReviewerState.REQUESTED_REVIEW;
}

/** Why `canRemoveReviewer` refused, for a toast. */
export function cannotRemoveReviewerMessage(login: string): string {
  return `Cannot remove ${login} as they have already reviewed the PR`;
}

/**
 * Effective selection state of one picker entry: pending changes win over the
 * entry's base membership on the PR.
 */
export function isPendingSelected(
  pending: PendingSelection,
  key: string,
  isBaseSelected: boolean,
): boolean {
  if (pending.toRemove.has(key)) {
    return false;
  }
  if (pending.toAdd.has(key)) {
    return true;
  }
  return isBaseSelected;
}

/**
 * Toggles one picker entry in the pending add/remove sets.
 * Deselecting a pending add (or selecting a pending remove) just cancels the
 * pending change; otherwise the entry moves to the opposite set.
 * Returns new Sets so previous snapshots are never mutated.
 */
export function togglePendingSelection(
  pending: PendingSelection,
  key: string,
  isBaseSelected: boolean,
): PendingSelection {
  const next = { toAdd: new Set(pending.toAdd), toRemove: new Set(pending.toRemove) };

  if (isPendingSelected(pending, key, isBaseSelected)) {
    if (next.toAdd.has(key)) {
      next.toAdd.delete(key);
    } else {
      next.toRemove.add(key);
    }
  } else {
    if (next.toRemove.has(key)) {
      next.toRemove.delete(key);
    } else {
      next.toAdd.add(key);
    }
  }

  return next;
}

export type TeamPickerModel = {
  /** Candidate teams, currently-requested first then alphabetical */
  sortedTeams: TeamReviewerSelection[];
  /** Slugs of teams currently requested on the PR */
  requestedTeamSlugs: Set<string>;
  teamsBySlug: Map<string, TeamReviewerSelection>;
};

/**
 * Builds the team picker candidates from the org's teams merged with the PR's
 * currently-requested teams (so a requested team missing from the org list can
 * still be unselected). Legacy PR metadata may carry team names without slugs;
 * those are resolved against the org's teams (the GitHub API takes slugs) and
 * fall back to the name.
 */
export function buildTeamPickerModel(
  requestedTeams: Array<{ name: string; slug: string | null }>,
  orgTeams: Array<{ slug: string; name: string }>,
): TeamPickerModel {
  const orgTeamList: TeamReviewerSelection[] = orgTeams.map((team) => ({
    slug: team.slug,
    name: team.name,
  }));
  const orgTeamsByName = new Map(orgTeamList.map((team) => [team.name.toLowerCase(), team]));

  const resolvedRequestedTeams: TeamReviewerSelection[] = requestedTeams.map((team) =>
    team.slug
      ? { slug: team.slug, name: team.name }
      : (orgTeamsByName.get(team.name.toLowerCase()) ?? { slug: team.name, name: team.name }),
  );
  const requestedTeamSlugs = new Set(resolvedRequestedTeams.map((team) => team.slug));

  const teamsBySlug = new Map<string, TeamReviewerSelection>();
  for (const team of [...orgTeamList, ...resolvedRequestedTeams]) {
    if (!teamsBySlug.has(team.slug)) {
      teamsBySlug.set(team.slug, team);
    }
  }

  const sortedTeams = Array.from(teamsBySlug.values()).sort((a, b) => {
    const aIsRequested = requestedTeamSlugs.has(a.slug);
    const bIsRequested = requestedTeamSlugs.has(b.slug);
    if (aIsRequested && !bIsRequested) {
      return -1;
    }
    if (!aIsRequested && bIsRequested) {
      return 1;
    }
    return a.name.localeCompare(b.name);
  });

  return { sortedTeams, requestedTeamSlugs, teamsBySlug };
}

/**
 * Sorts collaborators by their reviewer status and login name
 * @returns Sorted list of collaborators with current reviewers appearing first
 */
export function sortReviewers(reviewers: Reviewer[], availableReviewers: Collaborators) {
  const reviewerLogins = new Set(reviewers.map((reviewer) => reviewer.login));

  return [...availableReviewers].sort((a, b) => {
    const aIsReviewer = reviewerLogins.has(a.login);
    const bIsReviewer = reviewerLogins.has(b.login);
    if (aIsReviewer && !bIsReviewer) {
      return -1;
    }
    if (!aIsReviewer && bIsReviewer) {
      return 1;
    }
    return a.login.localeCompare(b.login);
  });
}
